/**
 * React wrapper around a single NiiVue WebGL canvas.
 *
 * Design notes worth keeping in mind before editing:
 *
 *  - `@niivue/niivue` is imported dynamically. It pulls in a large WebGL bundle
 *    and it throws at construction time on machines without WebGL2. A static
 *    import would take the whole app down with it; a dynamic one lets us show
 *    an honest error panel instead. This matters because the shipping shell is
 *    WebKitGTK, where WebGL2 is genuinely sometimes unavailable.
 *
 *  - The Niivue instance is created once per mounted pane and reused across
 *    volume changes (`loadVolumes` replaces the volume list internally). We do
 *    NOT tear the instance down when the URL changes: creating a new GL context
 *    per volume is exactly how NiiVue apps end up hitting the browser's
 *    ~16-context limit and going blank. Full teardown happens on unmount, and
 *    it explicitly asks the driver to drop the context via WEBGL_lose_context,
 *    because `cleanup()` alone leaves the context alive until GC.
 */

import * as React from "react";
import type { NVImage, NiiVueLocation, Niivue } from "@niivue/niivue";
import { cn } from "@/lib/utils";
import { registerInstance, setLinked, unregisterInstance } from "./nvRegistry";
import type {
  LocationInfo,
  MaskedIntensity,
  ViewPlane,
  ViewerStatus,
  ViewerStatsPayload,
  VolumeStats,
} from "./types";

export interface NiiVueCanvasProps {
  /** Stable identity for the link registry. Must be unique per mounted pane. */
  id: string;
  baseUrl: string | null;
  overlayUrl?: string | null;
  sliceType: ViewPlane;
  /** Overlay opacity, 0..1. Ignored when there is no overlay. */
  opacity: number;
  colormap: string;
  overlayColormap?: string;
  /** Overlay display window; maps onto the overlay's cal_min / cal_max. */
  overlayThreshold?: { min: number; max: number } | null;
  crosshair: boolean;
  linked: boolean;
  onLocationChange?: (loc: LocationInfo) => void;
  onStats?: (stats: ViewerStatsPayload) => void;
  onStatusChange?: (status: ViewerStatus, message: string | null) => void;
  /** Millimetres per CSS pixel in the first tile — drives the scale bar. */
  onScaleChange?: (mmPerPixel: number | null) => void;
  className?: string;
}

const WEBGL2_MESSAGE =
  "WebGL2 is not available in this window, so the image viewer cannot start. " +
  "On Linux this is usually the WebKitGTK compositing mode or a GPU driver without " +
  "WebGL2; try launching with WEBKIT_DISABLE_COMPOSITING_MODE=1, or update the driver.";

/** One probe for the whole app. Creating a context per pane leaks into WebKit's ~8-context cap. */
let webgl2Cached: boolean | null = null;

function webgl2Available(): boolean {
  if (webgl2Cached !== null) return webgl2Cached;
  try {
    const probe = document.createElement("canvas");
    probe.width = 8;
    probe.height = 8;
    const gl = probe.getContext("webgl2");
    webgl2Cached = gl !== null;
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    webgl2Cached = false;
  }
  return webgl2Cached;
}

/**
 * `getContext("webgl2")` on a canvas that has no context yet *creates* one.
 * Calling that from a React StrictMode cleanup (before NiiVue attached) then
 * losing it is exactly the `shaderSource` TypeError: the remount reuses the
 * lost context and `createShader` returns null.
 */
function loseOwnedContext(gl: WebGL2RenderingContext | null | undefined): void {
  if (!gl) return;
  try {
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    // The context may already be gone; nothing to release.
  }
}

function glOf(nv: Niivue | null): WebGL2RenderingContext | null {
  const gl = (nv as Niivue & { gl?: WebGL2RenderingContext | null })?.gl;
  return gl ?? null;
}

/**
 * Distinguishes a StrictMode remount (same canvas, new effect) from a real
 * unmount. The remount must not lose the WebGL context or the next attach
 * compiles shaders against a dead handle.
 */
const canvasGeneration = new WeakMap<HTMLCanvasElement, object>();

/** One attach at a time so three 2×2 panes do not compile shaders on a half-ready GPU. */
let attachGate: Promise<void> = Promise.resolve();

function withAttachGate<T>(fn: () => Promise<T>): Promise<T> {
  const run = attachGate.then(fn, fn);
  attachGate = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

function waitForLaidOut(canvas: HTMLCanvasElement, cancelled: () => boolean): Promise<boolean> {
  if (canvas.offsetWidth > 1 && canvas.offsetHeight > 1) return Promise.resolve(true);
  return new Promise((resolve) => {
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      ro.disconnect();
      window.clearInterval(poll);
      resolve(ok);
    };
    const ro = new ResizeObserver(() => {
      if (cancelled()) finish(false);
      else if (canvas.offsetWidth > 1 && canvas.offsetHeight > 1) finish(true);
    });
    ro.observe(canvas);
    if (canvas.parentElement) ro.observe(canvas.parentElement);
    const poll = window.setInterval(() => {
      if (cancelled()) finish(false);
      else if (canvas.offsetWidth > 1 && canvas.offsetHeight > 1) finish(true);
    }, 50);
  });
}

function initErrorMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error);
  if (/shader/i.test(raw)) {
    return (
      "The GPU context was lost while starting the viewer. " +
      "This is common on Linux when several panes start at once.\n" +
      "Use View → Single, then switch back to 2 × 2.\n" +
      raw
    );
  }
  return `The image viewer could not start.\n${raw}`;
}

/**
 * Statistics from the loaded voxels, not from the manifest.
 *
 * `global_min` / `global_max` come from NiiVue in scaled (scl_slope applied)
 * units, so we trust those. The non-zero count is our own pass over the first
 * frame only — for a 4D series the later frames are separate images, and
 * counting across them would silently multiply a mask volume.
 */
function computeStats(img: NVImage): VolumeStats | null {
  const dims = img.dimsRAS ?? img.dims ?? [];
  const pixDims = img.pixDimsRAS ?? img.pixDims ?? [];
  const data = img.img;
  if (!data || dims.length < 4) return null;

  const nx = Math.abs(dims[1]);
  const ny = Math.abs(dims[2]);
  const nz = Math.abs(dims[3]);
  const perFrame = nx * ny * nz;
  const end = Math.min(data.length, perFrame || data.length);
  let nonZeroVox = 0;
  let finiteVox = 0;
  let rawMin = Number.POSITIVE_INFINITY;
  let rawMax = Number.NEGATIVE_INFINITY;
  let mean = 0;
  let m2 = 0;
  let sx = 0;
  let sy = 0;
  let sz = 0;
  let minX = nx;
  let minY = ny;
  let minZ = nz;
  let maxX = -1;
  let maxY = -1;
  let maxZ = -1;
  for (let i = 0; i < end; i += 1) {
    const v = data[i];
    if (!Number.isFinite(v)) continue;
    finiteVox += 1;
    const delta = v - mean;
    mean += delta / finiteVox;
    m2 += delta * (v - mean);
    if (v < rawMin) rawMin = v;
    if (v > rawMax) rawMax = v;
    if (v <= 0) continue;
    nonZeroVox += 1;
    const x = i % nx;
    const y = Math.floor(i / nx) % ny;
    const z = Math.floor(i / (nx * ny));
    sx += x;
    sy += y;
    sz += z;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }

  const std = finiteVox > 1 ? Math.sqrt(m2 / (finiteVox - 1)) : 0;
  const { median, p5, p95 } = percentiles(data, end, rawMin, rawMax, finiteVox);
  const voxelMm3 = Math.abs((pixDims[1] ?? 0) * (pixDims[2] ?? 0) * (pixDims[3] ?? 0));
  const finiteMin = Number.isFinite(rawMin) ? rawMin : 0;
  const finiteMax = Number.isFinite(rawMax) ? rawMax : 0;
  const globalMin = img.global_min;
  const globalMax = img.global_max;
  const displayMin = img.cal_min;
  const displayMax = img.cal_max;
  return {
    min: globalMin != null && Number.isFinite(globalMin) ? globalMin : finiteMin,
    max: globalMax != null && Number.isFinite(globalMax) ? globalMax : finiteMax,
    calMin: displayMin != null && Number.isFinite(displayMin) ? displayMin : finiteMin,
    calMax: displayMax != null && Number.isFinite(displayMax) ? displayMax : finiteMax,
    nonZeroVox,
    volumeMl: (nonZeroVox * voxelMm3) / 1000,
    voxelMm3,
    finiteVox,
    mean,
    std,
    median,
    p5,
    p95,
    centroidVox:
      nonZeroVox > 0 ? [sx / nonZeroVox, sy / nonZeroVox, sz / nonZeroVox] : null,
    bboxVox:
      nonZeroVox > 0
        ? { min: [minX, minY, minZ], max: [maxX, maxY, maxZ] }
        : null,
    dims: [nx, ny, nz],
    pixDims: [pixDims[1] ?? 0, pixDims[2] ?? 0, pixDims[3] ?? 0],
  };
}

const HIST_BINS = 512;

function percentiles(
  data: ArrayLike<number>,
  end: number,
  min: number,
  max: number,
  n: number,
): { median: number | null; p5: number | null; p95: number | null } {
  if (n < 1 || !Number.isFinite(min) || !Number.isFinite(max)) {
    return { median: null, p5: null, p95: null };
  }
  if (min === max) return { median: min, p5: min, p95: min };
  const counts = new Uint32Array(HIST_BINS);
  const span = max - min;
  for (let i = 0; i < end; i += 1) {
    const v = data[i];
    if (!Number.isFinite(v)) continue;
    const b = Math.min(HIST_BINS - 1, Math.max(0, Math.floor(((v - min) / span) * HIST_BINS)));
    counts[b] += 1;
  }
  return {
    p5: percentileFromHist(counts, min, max, n, 5),
    median: percentileFromHist(counts, min, max, n, 50),
    p95: percentileFromHist(counts, min, max, n, 95),
  };
}

function percentileFromHist(counts: Uint32Array, min: number, max: number, n: number, pct: number): number {
  const target = (pct / 100) * Math.max(n - 1, 0);
  let acc = 0;
  const span = max - min;
  for (let b = 0; b < counts.length; b += 1) {
    const next = acc + counts[b];
    if (next > target || b === counts.length - 1) {
      const lo = min + (b / counts.length) * span;
      const hi = min + ((b + 1) / counts.length) * span;
      const inBin = counts[b] || 1;
      return lo + ((target - acc) / inBin) * (hi - lo);
    }
    acc = next;
  }
  return max;
}

function computeMaskedIntensity(base: NVImage, overlay: NVImage): MaskedIntensity | null {
  const baseDims = base.dimsRAS ?? base.dims ?? [];
  const overlayDims = overlay.dimsRAS ?? overlay.dims ?? [];
  const a = base.img;
  const b = overlay.img;
  if (!a || !b || baseDims.length < 4 || overlayDims.length < 4) return null;
  const sameGrid =
    Math.abs(baseDims[1]) === Math.abs(overlayDims[1]) &&
    Math.abs(baseDims[2]) === Math.abs(overlayDims[2]) &&
    Math.abs(baseDims[3]) === Math.abs(overlayDims[3]);
  if (!sameGrid) {
    return {
      compatible: false,
      voxels: 0,
      mean: null,
      std: null,
      min: null,
      max: null,
      backgroundMean: null,
      backgroundStd: null,
    };
  }
  const perFrame = Math.abs(baseDims[1] * baseDims[2] * baseDims[3]);
  const end = Math.min(a.length, b.length, perFrame || a.length);
  let n = 0;
  let mean = 0;
  let m2 = 0;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let bgN = 0;
  let bgMean = 0;
  let bgM2 = 0;
  for (let i = 0; i < end; i += 1) {
    const v = a[i];
    if (!Number.isFinite(v)) continue;
    if (b[i] > 0) {
      n += 1;
      const d = v - mean;
      mean += d / n;
      m2 += d * (v - mean);
      if (v < min) min = v;
      if (v > max) max = v;
    } else {
      bgN += 1;
      const d = v - bgMean;
      bgMean += d / bgN;
      bgM2 += d * (v - bgMean);
    }
  }
  return {
    compatible: true,
    voxels: n,
    mean: n > 0 ? mean : null,
    std: n > 1 ? Math.sqrt(m2 / (n - 1)) : n === 1 ? 0 : null,
    min: n > 0 ? min : null,
    max: n > 0 ? max : null,
    backgroundMean: bgN > 0 ? bgMean : null,
    backgroundStd: bgN > 1 ? Math.sqrt(bgM2 / (bgN - 1)) : bgN === 1 ? 0 : null,
  };
}

/** NiiVue exposes the slice-type enum values as instance fields. */
function sliceTypeValue(nv: Niivue, plane: ViewPlane): number {
  switch (plane) {
    case "axial":
      return nv.sliceTypeAxial;
    case "coronal":
      return nv.sliceTypeCoronal;
    case "sagittal":
      return nv.sliceTypeSagittal;
    case "render":
      return nv.sliceTypeRender;
    default:
      return nv.sliceTypeMultiplanar;
  }
}

export function NiiVueCanvas({
  id,
  baseUrl,
  overlayUrl,
  sliceType,
  opacity,
  colormap,
  overlayColormap = "warm",
  overlayThreshold,
  crosshair,
  linked,
  onLocationChange,
  onStats,
  onStatusChange,
  onScaleChange,
  className,
}: NiiVueCanvasProps) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const nvRef = React.useRef<Niivue | null>(null);
  const [status, setStatus] = React.useState<ViewerStatus>(baseUrl ? "loading" : "empty");
  const [message, setMessage] = React.useState<string | null>(null);
  /** Flips once attach succeeds, so the option effects know they may touch nv. */
  const [attached, setAttached] = React.useState(false);

  // Callbacks are held in refs: they are usually inline arrows, and putting them
  // in an effect's dependency list would re-run the effect on every render.
  const locationCb = React.useRef(onLocationChange);
  const statsCb = React.useRef(onStats);
  const statusCb = React.useRef(onStatusChange);
  const scaleCb = React.useRef(onScaleChange);
  locationCb.current = onLocationChange;
  statsCb.current = onStats;
  statusCb.current = onStatusChange;
  scaleCb.current = onScaleChange;

  const report = React.useCallback((next: ViewerStatus, detail: string | null) => {
    setStatus(next);
    setMessage(detail);
    statusCb.current?.(next, detail);
  }, []);

  /** Sample the on-screen scale from the first tile NiiVue actually drew. */
  const sampleScale = React.useCallback(() => {
    const nv = nvRef.current;
    const tile = nv?.screenSlices?.[0];
    if (!nv || !tile || !tile.fovMM?.[0] || !tile.leftTopWidthHeight?.[2]) {
      scaleCb.current?.(null);
      return;
    }
    // leftTopWidthHeight is in device pixels; the scale bar is laid out in CSS px.
    const devicePixelsPerMm = tile.leftTopWidthHeight[2] / tile.fovMM[0];
    const dpr = window.devicePixelRatio || 1;
    const cssPixelsPerMm = devicePixelsPerMm / dpr;
    scaleCb.current?.(cssPixelsPerMm > 0 ? 1 / cssPixelsPerMm : null);
  }, []);

  // --- instance lifecycle: mount and unmount only -------------------------
  React.useEffect(() => {
    const canvas = canvasRef.current;
    const generation = {};
    if (canvas) canvasGeneration.set(canvas, generation);
    let disposed = false;

    if (!webgl2Available()) {
      report("error", WEBGL2_MESSAGE);
      return;
    }

    void (async () => {
      try {
        const { Niivue: NiivueCtor } = await import("@niivue/niivue");
        await withAttachGate(async () => {
          if (disposed || !canvas) return;
          const ready = await waitForLaidOut(canvas, () => disposed);
          if (disposed || !ready) return;

          const nv = new NiivueCtor({
            backColor: [0, 0, 0, 1],
            // We draw the orientation letters, the ruler and the crosshair chrome
            // in the DOM so they match the rest of the application's typography.
            isOrientationTextVisible: false,
            isColorbar: false,
            isRuler: false,
            show3Dcrosshair: true,
            isOrientCube: false,
            crosshairWidth: 1,
            dragAndDropEnabled: false,
            loadingText: "",
            logLevel: "error",
          });

          nv.onLocationChange = (raw: unknown) => {
            const loc = raw as NiiVueLocation;
            if (!loc?.vox) return;
            locationCb.current?.({
              vox: [loc.vox[0], loc.vox[1], loc.vox[2]],
              mm: [loc.mm[0], loc.mm[1], loc.mm[2]],
              label: loc.string ?? "",
              values: (loc.values ?? []).map((v) => ({ name: v.name, value: v.value })),
            });
          };

          // Antialias is off: WebKitGTK's shader compiler fails more often with MSAA.
          await nv.attachToCanvas(canvas, false);
          if (disposed) {
            const gl = glOf(nv);
            nv.cleanup?.();
            if (canvasGeneration.get(canvas) === generation) loseOwnedContext(gl);
            return;
          }
          nvRef.current = nv;
          registerInstance(id, nv);
          setAttached(true);
        });
      } catch (error) {
        console.error("[viewer] NiiVue failed to initialise", error);
        if (!disposed) report("error", initErrorMessage(error));
      }
    })();

    return () => {
      disposed = true;
      unregisterInstance(id);
      const nv = nvRef.current;
      nvRef.current = null;
      setAttached(false);
      const gl = glOf(nv);
      try {
        nv?.cleanup?.();
      } catch (error) {
        console.warn("[viewer] cleanup failed", error);
      }
      // Only drop a context NiiVue created, and only if this canvas was not
      // immediately remounted (React StrictMode). Never call getContext here.
      queueMicrotask(() => {
        if (canvas && canvasGeneration.get(canvas) === generation) {
          loseOwnedContext(gl);
          canvasGeneration.delete(canvas);
        }
      });
    };
  }, [id, report]);

  // --- volumes ------------------------------------------------------------
  React.useEffect(() => {
    const nv = nvRef.current;
    if (!attached || !nv) return;
    let cancelled = false;

    if (!baseUrl) {
      // Mocked `get_asset_url` returns an empty string until the Rust side
      // lands; that is the empty state, not an error.
      report("empty", null);
      statsCb.current?.({ base: null, overlay: null, masked: null });
      scaleCb.current?.(null);
      return;
    }

    report("loading", null);
    void (async () => {
      try {
        // NiiVue picks the reader from the file extension; a `blob:` URL (the
        // browser build's assets) has none, so name it explicitly.
        const named = (url: string, n: string) => (url.startsWith("blob:") ? { name: `${n}.nii.gz` } : {});
        const list: { url: string; colormap: string; opacity: number; name?: string }[] = [
          { url: baseUrl, colormap, opacity: 1, ...named(baseUrl, "base") },
        ];
        if (overlayUrl) {
          list.push({ url: overlayUrl, colormap: overlayColormap, opacity, ...named(overlayUrl, "overlay") });
        }
        await nv.loadVolumes(list);
        if (cancelled) return;
        report("ready", null);
        statsCb.current?.({
          base: nv.volumes[0] ? computeStats(nv.volumes[0]) : null,
          overlay: nv.volumes[1] ? computeStats(nv.volumes[1]) : null,
          masked:
            nv.volumes[0] && nv.volumes[1]
              ? computeMaskedIntensity(nv.volumes[0], nv.volumes[1])
              : null,
        });
        sampleScale();
      } catch (error) {
        console.error("[viewer] loadVolumes failed", error);
        if (!cancelled) {
          report(
            "error",
            `Could not read this volume.\n${error instanceof Error ? error.message : String(error)}`,
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // `opacity`/`overlayColormap` are applied by their own effects below; they
    // are only initial values here, so they must not retrigger a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attached, baseUrl, overlayUrl, colormap, report, sampleScale]);

  // --- cheap option updates ----------------------------------------------
  React.useEffect(() => {
    const nv = nvRef.current;
    if (!attached || !nv) return;
    nv.setSliceType(sliceTypeValue(nv, sliceType));
    nv.opts.isOrientCube = sliceType === "render";
    sampleScale();
  }, [attached, sliceType, sampleScale]);

  React.useEffect(() => {
    const nv = nvRef.current;
    if (!attached || !nv || nv.volumes.length < 2) return;
    nv.setOpacity(1, opacity);
  }, [attached, opacity, status]);

  React.useEffect(() => {
    const nv = nvRef.current;
    if (!attached || !nv || nv.volumes.length < 2) return;
    nv.setColormap(nv.volumes[1].id, overlayColormap);
  }, [attached, overlayColormap, status]);

  React.useEffect(() => {
    const nv = nvRef.current;
    if (!attached || !nv || nv.volumes.length < 2 || !overlayThreshold) return;
    const overlay = nv.volumes[1];
    overlay.cal_min = overlayThreshold.min;
    overlay.cal_max = overlayThreshold.max;
    nv.updateGLVolume();
  }, [attached, overlayThreshold, status]);

  React.useEffect(() => {
    const nv = nvRef.current;
    if (!attached || !nv) return;
    nv.setCrosshairWidth(crosshair ? 1 : 0);
  }, [attached, crosshair]);

  React.useEffect(() => {
    // Linking is a property of the whole grid, not of one pane, so the registry
    // owns the topology. Every pane gets the same prop, making this idempotent.
    if (!attached) return;
    setLinked(linked);
  }, [attached, linked]);

  // Re-sample the scale bar when the pane is resized.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => sampleScale());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [sampleScale]);

  return (
    <div className={cn("relative h-full w-full overflow-hidden bg-ads-viewer", className)}>
      <canvas ref={canvasRef} className="block h-full w-full" />

      {status === "empty" ? (
        <StatePanel
          title="No volume loaded"
          body="Pick a base volume in the Study Explorer. Asset URLs come from the backend; while it is mocked they are empty and nothing can be drawn."
        />
      ) : null}

      {status === "loading" ? (
        <StatePanel title="Loading volume…" body="Reading the NIfTI header and uploading textures." />
      ) : null}

      {status === "error" ? <StatePanel title="Viewer unavailable" body={message ?? ""} tone="error" /> : null}
    </div>
  );
}

/**
 * Every non-ready state gets a readable panel. A blank black rectangle is
 * indistinguishable from a volume of zeros, which is a real thing to load.
 */
function StatePanel({
  title,
  body,
  tone = "neutral",
}: {
  title: string;
  body: string;
  tone?: "neutral" | "error";
}) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-ads-viewer/80 p-4">
      <div
        className={cn(
          "max-w-sm rounded border px-4 py-3 text-center",
          tone === "error"
            ? "border-red-900 bg-red-950/60 text-red-200"
            : "border-ads-border-strong bg-ads-subtle/90 text-ads-text",
        )}
      >
        <p className="text-xs font-semibold">{title}</p>
        <p className="mt-1 whitespace-pre-line text-[11px] leading-relaxed text-ads-muted">{body}</p>
      </div>
    </div>
  );
}
